import {
  faArrowLeft,
  faCodeBranch,
  faFilePdf,
  faLink,
  faMagnifyingGlass,
  faPen,
  faPlus,
  faPrint,
  faRobot,
  faStar,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  type ClipboardEvent as ReactClipboardEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import React from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import type { BallotChoice, GuideItemDetail } from "../../shared/contracts";
import { api, ApiClientError, isAbortError } from "../api";
import { useAuth } from "../auth";
import { useDialogFocusTrap } from "../components/useDialogFocusTrap";
import { useDocumentTitle } from "../document-title";
import { startPolling } from "../polling";
import { normalizeSourceUrl } from "../source-url";

export interface GuideDetail {
  id: string;
  code: string;
  accountSlug: string;
  ownerName: string;
  title: string;
  description: string;
  guideDate: string;
  hearingType: string;
  visibility: "public" | "private";
  status: "draft" | "published" | "archived";
  version: number;
  publishedAt: string | null;
  canContribute: boolean;
  canManage: boolean;
  isFollowing: boolean;
  items: GuideItemDetail[];
  itemPage: number;
  itemPageSize: number;
  itemTotal: number;
  guideItemCount: number;
  pdfDescriptionStatus:
    "pending" | "processing" | "completed" | "failed" | null;
  pendingSourcePreviews: number;
  publicationRoundCount: number;
  pendingAdoptionCount: number;
  currentItemIndex: number | null;
  previousItemCode: string | null;
  nextItemCode: string | null;
  otherPositions: Array<{
    code: string;
    accountSlug: string;
    ownerName: string;
    title: string;
  }>;
}

interface GuideComparisonOption {
  id: string;
  code: string;
  accountSlug: string;
  ownerName: string;
  title: string;
  guideDate: string;
  visibility: "public" | "private";
  status: "draft" | "published" | "archived";
  matchCount: number;
  itemCount: number;
}

interface GuideComparison {
  guide: Pick<
    GuideComparisonOption,
    | "id"
    | "code"
    | "accountSlug"
    | "ownerName"
    | "title"
    | "visibility"
    | "status"
  >;
  positions: Record<
    string,
    Array<{
      order: number;
      label: string;
      adoptedChoice: BallotChoice | null;
    }>
  >;
}

const positionLabels = {
  approve: "Approve",
  disapprove: "Disapprove",
  no_position: "No Position",
} as const;

const positionChoices = Object.entries(positionLabels) as Array<
  [BallotChoice, string]
>;

function requireSourceUrlForLabel(
  sourceUrl: string | null,
  sourceLabel: string | null,
) {
  if (sourceLabel && !sourceUrl) throw new Error("SOURCE_LABEL_REQUIRES_URL");
}

function sourceFieldError(error: unknown): string | null {
  if (!(error instanceof Error)) return null;
  if (error.message === "INVALID_SOURCE_URL")
    return "Enter a valid source URL.";
  if (error.message === "SOURCE_LABEL_REQUIRES_URL")
    return "Enter a source URL before adding a source label.";
  return null;
}

function defaultRoundLabels(hearingType: GuideDetail["hearingType"]) {
  if (hearingType === "two_vote") return ["First hearing", "Final hearing"];
  if (hearingType === "three_vote")
    return ["First hearing", "Second hearing", "Final hearing"];
  if (hearingType === "four_vote")
    return [
      "First hearing",
      "Second hearing",
      "Third hearing",
      "Final hearing",
    ];
  return ["Position"];
}

interface BulkAddRow {
  agendaNumber: string;
  description: string;
  sourceUrl: string;
  sourceLabel: string;
}

const bulkAddColumns = [
  "agendaNumber",
  "description",
  "sourceUrl",
  "sourceLabel",
] as const;

function blankBulkAddRows(count = 8): BulkAddRow[] {
  return Array.from({ length: count }, () => ({
    agendaNumber: "",
    description: "",
    sourceUrl: "",
    sourceLabel: "",
  }));
}

type SaveState = "saving" | "error";

interface StanceControlProps {
  label: string;
  currentChoice: BallotChoice | null;
  canEdit: boolean;
  isEditing: boolean;
  state: SaveState | undefined;
  changeLabel: string;
  onEdit: () => void;
  onSelect: (choice: BallotChoice | null) => void;
  onCancel: () => void;
}

function StanceControl({
  label,
  currentChoice,
  canEdit,
  isEditing,
  state,
  changeLabel,
  onEdit,
  onSelect,
  onCancel,
}: StanceControlProps) {
  return (
    <div className="inline-adoption">
      {canEdit && isEditing ? (
        <div
          className="inline-adoption-picker"
          role="group"
          aria-label={`Set ${label}`}
        >
          {positionChoices.map(([choice, choiceLabel]) => (
            <button
              type="button"
              className={`inline-adoption-choice ${choice}${currentChoice === choice ? " selected" : ""}`}
              aria-label={`${label}: ${choiceLabel}`}
              aria-pressed={currentChoice === choice}
              disabled={state === "saving"}
              key={choice}
              onClick={() => onSelect(currentChoice === choice ? null : choice)}
            >
              {choiceLabel}
            </button>
          ))}
          <button
            type="button"
            className="inline-adoption-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      ) : canEdit ? (
        <button
          type="button"
          className={`position-chip position-chip-button ${currentChoice ?? "pending"}`}
          title={changeLabel}
          aria-label={`${label}: ${currentChoice ? positionLabels[currentChoice] : "Pending"}. ${changeLabel}`}
          aria-expanded={isEditing}
          onClick={onEdit}
        >
          {currentChoice ? positionLabels[currentChoice] : "Pending"}
        </button>
      ) : currentChoice ? (
        <span
          className={`position-chip ${currentChoice}`}
          title={`${label}: ${positionLabels[currentChoice]}`}
        >
          {positionLabels[currentChoice]}
        </span>
      ) : (
        <span className="blank-stance" aria-label={`${label}: not set`}>
          —
        </span>
      )}
      {state === "saving" || state === "error" ? (
        <span className={`inline-adoption-state ${state}`} aria-live="polite">
          {state === "saving" ? "Saving…" : "Not saved"}
        </span>
      ) : null}
    </div>
  );
}

function ComparisonStances({
  comparison,
  itemCode,
}: {
  comparison: GuideComparison | null;
  itemCode: string;
}) {
  if (!comparison)
    return (
      <span className="blank-stance" aria-label="No comparison selected" />
    );
  const positions = comparison.positions[itemCode] ?? [];
  if (!positions.length)
    return (
      <span
        className="blank-stance"
        aria-label={`${comparison.guide.ownerName}: no matching position`}
      >
        —
      </span>
    );
  return (
    <div className="comparison-position-list">
      {positions.map((position) =>
        position.adoptedChoice ? (
          <span
            className={`position-chip ${position.adoptedChoice}`}
            key={position.order}
            title={`${position.label}: ${positionLabels[position.adoptedChoice]}`}
          >
            {positionLabels[position.adoptedChoice]}
          </span>
        ) : (
          <span
            className="blank-stance"
            aria-label={`${position.label}: pending`}
            key={position.order}
          >
            —
          </span>
        ),
      )}
    </div>
  );
}

export function GuideDetailPage() {
  const { accountSlug = "", code = "" } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedItemPage = Number(searchParams.get("itemPage") ?? 1);
  const itemPage =
    Number.isInteger(requestedItemPage) && requestedItemPage > 0
      ? requestedItemPage
      : 1;
  const requestedItemPageSize = Number(searchParams.get("itemPageSize") ?? 100);
  const itemPageSize =
    requestedItemPageSize === 50 || requestedItemPageSize === 100
      ? requestedItemPageSize
      : 100;
  const itemQuery = searchParams.get("itemQ") ?? "";
  const firstComparisonId = searchParams.get("compare1");
  const secondComparisonId = searchParams.get("compare2");
  const [itemQueryDraft, setItemQueryDraft] = useState(itemQuery);
  const [guide, setGuide] = useState<GuideDetail | null>(null);
  const [loadingGuide, setLoadingGuide] = useState(true);
  const [error, setError] = useState("");
  const [publishState, setPublishState] = useState<SaveState>();
  const [guideSaveState, setGuideSaveState] = useState<SaveState>();
  const [itemSaveStates, setItemSaveStates] = useState<
    Record<string, SaveState>
  >({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [editingGuide, setEditingGuide] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingVoteRoundId, setEditingVoteRoundId] = useState<string | null>(
    null,
  );
  const [editingRoundId, setEditingRoundId] = useState<string | null>(null);
  const [adoptionStates, setAdoptionStates] = useState<
    Record<string, SaveState>
  >({});
  const [voteStates, setVoteStates] = useState<Record<string, SaveState>>({});
  const [comparisonColumns, setComparisonColumns] = useState<
    [GuideComparison | null, GuideComparison | null]
  >([null, null]);
  const [comparisonDialogSlot, setComparisonDialogSlot] = useState<
    0 | 1 | null
  >(null);
  const [comparisonOptions, setComparisonOptions] = useState<
    GuideComparisonOption[]
  >([]);
  const [comparisonQuery, setComparisonQuery] = useState("");
  const [comparisonBusy, setComparisonBusy] = useState(false);
  const [comparisonError, setComparisonError] = useState("");
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemState, setAddItemState] = useState<SaveState>();
  const [bulkAddRows, setBulkAddRows] = useState<BulkAddRow[]>(() =>
    blankBulkAddRows(),
  );
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkSaveState, setBulkSaveState] = useState<SaveState>();
  const [followState, setFollowState] = useState<SaveState>();
  const [forkState, setForkState] = useState<SaveState>();
  useDocumentTitle(guide?.title ?? "Position guide");
  const modalDialogRef = useDialogFocusTrap(
    comparisonDialogSlot !== null || deleteDialogOpen,
    () => {
      if (comparisonDialogSlot !== null) {
        setComparisonDialogSlot(null);
        return;
      }
      if (!deleteBusy) {
        setDeleteError("");
        setDeleteDialogOpen(false);
      }
    },
  );

  useEffect(() => {
    if (requestedItemPage === itemPage) return;
    const next = new URLSearchParams(searchParams);
    next.set("itemPage", String(itemPage));
    setSearchParams(next, { replace: true });
  }, [itemPage, requestedItemPage, searchParams, setSearchParams]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      const query = new URLSearchParams({
        itemPage: String(itemPage),
        itemPageSize: String(itemPageSize),
      });
      if (itemQuery) query.set("itemQ", itemQuery);
      setError("");
      return api<GuideDetail>(
        `/api/guides/${encodeURIComponent(accountSlug)}/${encodeURIComponent(code)}?${query.toString()}`,
        { signal: signal ?? null },
      ).then((loadedGuide) => {
        setGuide(loadedGuide);
        return loadedGuide;
      });
    },
    [accountSlug, code, itemPage, itemPageSize, itemQuery],
  );

  useEffect(() => {
    const controller = new AbortController();
    setGuide((current) =>
      current?.accountSlug === accountSlug && current.code === code
        ? current
        : null,
    );
    setLoadingGuide(true);
    void load(controller.signal)
      .catch((caught: unknown) => {
        if (isAbortError(caught)) return;
        setError(
          caught instanceof ApiClientError
            ? caught.message
            : "Unable to load guide.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingGuide(false);
      });
    return () => controller.abort();
  }, [accountSlug, code, load]);

  const retryLoad = () => {
    setGuide(null);
    setLoadingGuide(true);
    void load()
      .catch((caught: unknown) =>
        setError(
          caught instanceof ApiClientError
            ? caught.message
            : "Unable to load guide.",
        ),
      )
      .finally(() => setLoadingGuide(false));
  };

  useEffect(() => {
    if (
      !guide ||
      (guide.pdfDescriptionStatus !== "pending" &&
        guide.pdfDescriptionStatus !== "processing" &&
        guide.pendingSourcePreviews < 1)
    )
      return;
    return startPolling({
      request: load,
      onValue: () => undefined,
      onError: (caught) =>
        setError(
          caught instanceof ApiClientError
            ? caught.message
            : "Unable to refresh PDF processing status.",
        ),
      shouldContinue: (updated) =>
        updated.pdfDescriptionStatus === "pending" ||
        updated.pdfDescriptionStatus === "processing" ||
        updated.pendingSourcePreviews > 0,
      delayMs: 5_000,
    });
  }, [guide, load]);

  useEffect(() => {
    setItemQueryDraft(itemQuery);
  }, [itemQuery]);

  useEffect(() => {
    if (!guide || guide.itemPage !== itemPage) return;
    const lastPage = Math.max(
      1,
      Math.ceil(guide.itemTotal / guide.itemPageSize),
    );
    if (itemPage <= lastPage) return;
    const next = new URLSearchParams(searchParams);
    next.set("itemPage", String(lastPage));
    setSearchParams(next, { replace: true });
  }, [guide, itemPage, searchParams, setSearchParams]);

  const visibleGuideId = guide?.id ?? null;
  const visibleItemCodes =
    guide?.items.map((item) => item.code).join(",") ?? "";
  useEffect(() => {
    if (!visibleGuideId || !visibleItemCodes) {
      setComparisonColumns([null, null]);
      return;
    }
    setComparisonColumns([null, null]);
    const comparisonIds = [firstComparisonId, secondComparisonId] as const;
    let cancelled = false;
    void Promise.all(
      comparisonIds.map((comparisonId) =>
        comparisonId
          ? api<GuideComparison>(
              `/api/guides/${visibleGuideId}/comparisons/${comparisonId}?itemCodes=${encodeURIComponent(visibleItemCodes)}`,
            )
          : Promise.resolve(null),
      ),
    )
      .then((columns) => {
        if (!cancelled)
          setComparisonColumns([columns[0] ?? null, columns[1] ?? null]);
      })
      .catch((caught: unknown) => {
        if (!cancelled)
          setError(
            caught instanceof ApiClientError
              ? caught.message
              : "Unable to load comparison positions.",
          );
      });
    return () => {
      cancelled = true;
    };
  }, [visibleGuideId, visibleItemCodes, firstComparisonId, secondComparisonId]);

  const openComparisonDialog = async (slot: 0 | 1) => {
    if (!guide) return;
    setComparisonDialogSlot(slot);
    setComparisonQuery("");
    setComparisonError("");
    setComparisonBusy(true);
    try {
      const response = await api<{ items: GuideComparisonOption[] }>(
        `/api/guides/${guide.id}/comparison-options`,
      );
      setComparisonOptions(response.items);
    } catch (caught) {
      setComparisonError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to load available agendas.",
      );
    } finally {
      setComparisonBusy(false);
    }
  };

  const selectComparison = (guideId: string | null) => {
    if (comparisonDialogSlot === null) return;
    const parameter = comparisonDialogSlot === 0 ? "compare1" : "compare2";
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (guideId) next.set(parameter, guideId);
      else next.delete(parameter);
      return next;
    });
    setComparisonDialogSlot(null);
  };

  useEffect(() => {
    const normalized = itemQueryDraft.trim();
    if (normalized === itemQuery) return;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (normalized) next.set("itemQ", normalized);
      else next.delete("itemQ");
      next.set("itemPage", "1");
      setSearchParams(next, { replace: true });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [itemQuery, itemQueryDraft, searchParams, setSearchParams]);

  if (error && !guide)
    return (
      <div className="page-section">
        <div className="container">
          <div className="alert alert-error" role="alert">
            <p>{error}</p>
            <button
              className="button button-small button-secondary"
              type="button"
              onClick={retryLoad}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  if (!guide)
    return (
      <div className="page-section">
        <div className="container loading-block" role="status">
          Loading guide…
        </div>
      </div>
    );

  const toggleFollow = async () => {
    const nextFollowing = !guide.isFollowing;
    setFollowState("saving");
    setError("");
    try {
      await api(`/api/guides/${guide.id}/follow`, {
        method: nextFollowing ? "PUT" : "DELETE",
      });
      setGuide((current) =>
        current ? { ...current, isFollowing: nextFollowing } : current,
      );
      setFollowState(undefined);
    } catch (caught) {
      setFollowState("error");
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to update following.",
      );
    }
  };
  const fork = async () => {
    setForkState("saving");
    setError("");
    try {
      const created = await api<{ accountSlug: string; code: string }>(
        `/api/guides/${guide.id}/fork`,
        { method: "POST" },
      );
      navigate(`/guides/${created.accountSlug}/${created.code}`);
    } catch (caught) {
      setForkState("error");
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to create a private copy.",
      );
    }
  };
  const publish = async () => {
    setPublishState("saving");
    setError("");
    try {
      await api(`/api/guides/${guide.id}/publish`, { method: "POST" });
      await load();
      setPublishState(undefined);
    } catch (caught) {
      setPublishState("error");
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to publish positions.",
      );
    }
  };
  const saveGuide = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setGuideSaveState("saving");
    setPublishState(undefined);
    setError("");
    try {
      await api(`/api/guides/${guide.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: form.get("title"),
          description: form.get("description"),
          guideDate: form.get("guideDate"),
          visibility: form.get("visibility"),
        }),
      });
      await load();
      setEditingGuide(false);
      setGuideSaveState(undefined);
    } catch (caught) {
      setGuideSaveState("error");
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to save guide details.",
      );
    }
  };
  const saveItem = async (
    event: FormEvent<HTMLFormElement>,
    itemId: string,
  ) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setItemSaveStates((current) => ({ ...current, [itemId]: "saving" }));
    setPublishState(undefined);
    setError("");
    try {
      const agendaNumber =
        String(form.get("agendaNumber") ?? "").trim() || null;
      const description = String(form.get("description") ?? "").trim();
      const sourceUrl = normalizeSourceUrl(form.get("sourceUrl"));
      const sourceLabel = String(form.get("sourceLabel") ?? "").trim() || null;
      requireSourceUrlForLabel(sourceUrl, sourceLabel);
      await api(`/api/guides/items/${itemId}`, {
        method: "PATCH",
        body: JSON.stringify({
          agendaNumber,
          description,
          sourceUrl,
          sourceLabel,
        }),
      });
      setGuide((current) =>
        current
          ? {
              ...current,
              status: "draft",
              items: current.items.map((item) =>
                item.id === itemId
                  ? {
                      ...item,
                      agendaNumber,
                      description,
                      sourceUrl,
                      sourceLabel,
                    }
                  : item,
              ),
            }
          : current,
      );
      setEditingItemId(null);
      setItemSaveStates((current) => {
        const next = { ...current };
        delete next[itemId];
        return next;
      });
    } catch (caught) {
      setItemSaveStates((current) => ({ ...current, [itemId]: "error" }));
      setError(
        sourceFieldError(caught) ??
          (caught instanceof ApiClientError
            ? caught.message
            : "Unable to save the agenda item."),
      );
    }
  };
  const updateBulkAddCell = (
    rowIndex: number,
    column: (typeof bulkAddColumns)[number],
    value: string,
  ) => {
    setBulkAddRows((current) =>
      current.map((row, index) =>
        index === rowIndex ? { ...row, [column]: value } : row,
      ),
    );
  };
  const pasteBulkAddCells = (
    event: ReactClipboardEvent<HTMLInputElement>,
    rowIndex: number,
    columnIndex: number,
  ) => {
    const raw = event.clipboardData.getData("text/plain");
    if (!raw.includes("\t") && !/[\r\n]/.test(raw)) return;
    event.preventDefault();
    const pastedRows = raw
      .replaceAll("\r\n", "\n")
      .replaceAll("\r", "\n")
      .split("\n");
    if (pastedRows.at(-1) === "") pastedRows.pop();
    setBulkAddRows((current) => {
      const required = rowIndex + pastedRows.length;
      const next = [
        ...current.map((row) => ({ ...row })),
        ...blankBulkAddRows(Math.max(0, required - current.length)),
      ];
      pastedRows.forEach((line, pastedRowIndex) => {
        line.split("\t").forEach((value, pastedColumnIndex) => {
          const column = bulkAddColumns[columnIndex + pastedColumnIndex];
          const target = next[rowIndex + pastedRowIndex];
          if (column && target) target[column] = value.trim();
        });
      });
      return next;
    });
  };
  const extendBulkAddOnTab = (
    event: ReactKeyboardEvent<HTMLInputElement>,
    rowIndex: number,
    columnIndex: number,
  ) => {
    if (
      event.key !== "Tab" ||
      event.shiftKey ||
      columnIndex !== bulkAddColumns.length - 1 ||
      rowIndex !== bulkAddRows.length - 1
    )
      return;
    event.preventDefault();
    setBulkAddRows((current) => [...current, ...blankBulkAddRows(1)]);
    window.setTimeout(() => {
      document
        .querySelector<HTMLInputElement>(
          `[data-bulk-add-cell="${String(rowIndex + 1)}-0"]`,
        )
        ?.focus();
    });
  };
  const addItems = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!guide) return;
    setAddItemState("saving");
    setError("");
    try {
      const rows = bulkAddRows.filter((row) =>
        Object.values(row).some((value) => value.trim()),
      );
      if (!rows.length) {
        setAddItemState(undefined);
        setError("Enter at least one agenda item.");
        return;
      }
      const items = rows.map((row) => {
        const sourceUrl = normalizeSourceUrl(row.sourceUrl);
        const sourceLabel = row.sourceLabel.trim() || null;
        requireSourceUrlForLabel(sourceUrl, sourceLabel);
        return {
          agendaNumber: row.agendaNumber.trim() || null,
          description: row.description.trim(),
          sourceUrl,
          sourceLabel,
          rounds: defaultRoundLabels(guide.hearingType).map((label) => ({
            label,
            description: "",
            occurredOn: null,
          })),
        };
      });
      await api(`/api/guides/${guide.id}/items/bulk`, {
        method: "POST",
        body: JSON.stringify({ items }),
      });
      setAddItemOpen(false);
      setBulkAddRows(blankBulkAddRows());
      setAddItemState(undefined);
      const nextPage = Math.ceil(
        (guide.guideItemCount + items.length) / guide.itemPageSize,
      );
      const next = new URLSearchParams(searchParams);
      next.delete("itemQ");
      next.set("itemPage", String(Math.max(1, nextPage)));
      setSearchParams(next);
      if (!itemQuery && itemPage === nextPage) await load();
    } catch (caught) {
      setAddItemState("error");
      setError(
        sourceFieldError(caught) ??
          (caught instanceof ApiClientError
            ? caught.message
            : "Unable to add the agenda items."),
      );
    }
  };
  const bulkSaveItems = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!guide) return;
    const form = new FormData(event.currentTarget);
    setBulkSaveState("saving");
    setError("");
    try {
      const items = guide.items.map((item) => {
        const sourceUrl = normalizeSourceUrl(form.get(`sourceUrl:${item.id}`));
        const sourceLabel =
          String(form.get(`sourceLabel:${item.id}`) ?? "").trim() || null;
        requireSourceUrlForLabel(sourceUrl, sourceLabel);
        return {
          id: item.id,
          agendaNumber:
            String(form.get(`agendaNumber:${item.id}`) ?? "").trim() || null,
          description: String(form.get(`description:${item.id}`) ?? "").trim(),
          sourceUrl,
          sourceLabel,
        };
      });
      await api(`/api/guides/${guide.id}/items`, {
        method: "PATCH",
        body: JSON.stringify({ items }),
      });
      setGuide((current) =>
        current
          ? {
              ...current,
              status: "draft",
              items: current.items.map((item) => {
                const updated = items.find((entry) => entry.id === item.id);
                return updated ? { ...item, ...updated } : item;
              }),
            }
          : current,
      );
      setBulkEditOpen(false);
      setBulkSaveState(undefined);
    } catch (caught) {
      setBulkSaveState("error");
      setError(
        sourceFieldError(caught) ??
          (caught instanceof ApiClientError
            ? caught.message
            : "Unable to save the shown agenda items."),
      );
    }
  };
  const votePosition = async (
    roundId: string,
    choice: BallotChoice | null,
    rationale: string,
  ) => {
    setVoteStates((current) => ({ ...current, [roundId]: "saving" }));
    setError("");
    try {
      await api(`/api/guides/rounds/${roundId}/vote`, {
        method: "PUT",
        body: JSON.stringify({ choice, rationale }),
      });
      setGuide((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) => ({
                ...item,
                rounds: item.rounds.map((round) =>
                  round.id === roundId ? { ...round, myChoice: choice } : round,
                ),
              })),
            }
          : current,
      );
      setEditingVoteRoundId(null);
      setVoteStates((current) => {
        const next = { ...current };
        delete next[roundId];
        return next;
      });
    } catch (caught) {
      setVoteStates((current) => ({ ...current, [roundId]: "error" }));
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to save personal position.",
      );
    }
  };
  const adoptPosition = async (
    roundId: string,
    choice: BallotChoice | null,
    rationale: string,
  ) => {
    setAdoptionStates((current) => ({ ...current, [roundId]: "saving" }));
    setPublishState(undefined);
    setError("");
    try {
      await api(`/api/guides/rounds/${roundId}/adoption`, {
        method: "PUT",
        body: JSON.stringify({ choice, rationale }),
      });
      setGuide((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) => ({
                ...item,
                rounds: item.rounds.map((round) =>
                  round.id === roundId
                    ? { ...round, adoptedChoice: choice }
                    : round,
                ),
              })),
            }
          : current,
      );
      setEditingRoundId(null);
      setAdoptionStates((current) => {
        const next = { ...current };
        delete next[roundId];
        return next;
      });
    } catch (caught) {
      setAdoptionStates((current) => ({ ...current, [roundId]: "error" }));
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to set organizational position.",
      );
    }
  };
  const setItemPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("itemPage", String(page));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const setItemPageSize = (pageSize: 50 | 100) => {
    const next = new URLSearchParams(searchParams);
    next.set("itemPage", "1");
    next.set("itemPageSize", String(pageSize));
    setSearchParams(next);
  };
  const totalPages = Math.max(
    1,
    Math.ceil(guide.itemTotal / guide.itemPageSize),
  );
  const normalizedComparisonQuery = comparisonQuery.trim().toLocaleLowerCase();
  const otherSelectedComparisonId =
    comparisonDialogSlot === 0 ? secondComparisonId : firstComparisonId;
  const filteredComparisonOptions = comparisonOptions.filter(
    (option) =>
      option.id !== otherSelectedComparisonId &&
      (!normalizedComparisonQuery ||
        `${option.ownerName} ${option.title} ${option.code}`
          .toLocaleLowerCase()
          .includes(normalizedComparisonQuery)),
  );
  const publicationRoundCount =
    guide.publicationRoundCount ??
    guide.items.reduce((count, item) => count + item.rounds.length, 0);
  const pendingAdoptionCount =
    guide.pendingAdoptionCount ??
    guide.items.reduce(
      (count, item) =>
        count + item.rounds.filter((round) => !round.adoptedChoice).length,
      0,
    );
  const adoptedPositionCount = Math.max(
    0,
    publicationRoundCount - pendingAdoptionCount,
  );
  const descriptionsInProgress =
    guide.pdfDescriptionStatus === "pending" ||
    guide.pdfDescriptionStatus === "processing";
  const publicationReady =
    guide.guideItemCount > 0 &&
    publicationRoundCount > 0 &&
    pendingAdoptionCount === 0 &&
    !descriptionsInProgress;
  const hasPublishedReport = guide.version > 0;
  const canPreviewDraft = guide.status === "draft" && guide.canManage;

  return (
    <div className="page-section guide-page dense-record-page">
      <div className="container">
        <div className="record-command-bar">
          <Link
            className="back-link"
            to={guide.canContribute ? "/library?scope=mine" : "/library"}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </Link>
          <div className="record-command-actions">
            {guide.canManage ? (
              <button
                className="icon-action"
                type="button"
                aria-pressed={editingGuide}
                disabled={guideSaveState === "saving"}
                onClick={() => {
                  setGuideSaveState(undefined);
                  setEditingGuide((current) => !current);
                  setEditingItemId(null);
                }}
              >
                <FontAwesomeIcon icon={faPen} />
                <span>{editingGuide ? "Cancel edit" : "Edit guide"}</span>
              </button>
            ) : null}
            {guide.canManage ? (
              <button
                className="icon-action delete-agenda-action"
                type="button"
                onClick={() => {
                  setDeleteError("");
                  setDeleteDialogOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faTrashCan} />
                <span>Delete agenda</span>
              </button>
            ) : null}
            {canPreviewDraft ? (
              <a
                className="icon-action"
                href={`/api/guides/${guide.id}/report.pdf?draft=1`}
                title="Download a PDF preview of the current draft"
              >
                <FontAwesomeIcon icon={faFilePdf} />
                <span>Preview PDF</span>
              </a>
            ) : null}
            {canPreviewDraft ? (
              <a
                className="icon-action"
                href={`/api/guides/${guide.id}/report.html?draft=1&print=1`}
                target="_blank"
                rel="noreferrer"
                title="Open a print view of the current draft"
              >
                <FontAwesomeIcon icon={faPrint} /> <span>Print draft</span>
              </a>
            ) : null}
            {hasPublishedReport ? (
              <a
                className="icon-action"
                href={`/api/guides/${guide.id}/report.pdf`}
                title="Download the latest immutable published PDF"
              >
                <FontAwesomeIcon icon={faFilePdf} />
                <span>
                  {guide.status === "published"
                    ? "Export PDF"
                    : "Published PDF"}
                </span>
              </a>
            ) : null}
            {hasPublishedReport && !canPreviewDraft ? (
              <a
                className="icon-action"
                href={`/api/guides/${guide.id}/report.html?print=1`}
                target="_blank"
                rel="noreferrer"
                title="Open the latest published guide for printing"
              >
                <FontAwesomeIcon icon={faPrint} /> <span>Print</span>
              </a>
            ) : null}
            {user ? (
              <button
                className="icon-action"
                type="button"
                aria-pressed={guide.isFollowing}
                disabled={followState === "saving"}
                onClick={() => void toggleFollow()}
              >
                <FontAwesomeIcon icon={faStar} />
                <span>
                  {followState === "saving"
                    ? "Updating…"
                    : guide.isFollowing
                      ? "Following"
                      : "Follow"}
                </span>
              </button>
            ) : null}
            {user ? (
              <button
                className="button button-small"
                type="button"
                disabled={forkState === "saving"}
                onClick={() => void fork()}
              >
                <FontAwesomeIcon icon={faCodeBranch} />
                {forkState === "saving" ? "Creating copy…" : "Fork"}
              </button>
            ) : null}
          </div>
        </div>

        <article className="guide-record-header compact-record-header">
          <p className="kicker">{guide.ownerName} STANCEKIT</p>
          {guide.canManage && editingGuide ? (
            <form
              className="guide-inline-edit-form"
              onSubmit={(event) => void saveGuide(event)}
            >
              <label>
                Title
                <input name="title" required defaultValue={guide.title} />
              </label>
              <label>
                Description
                <textarea
                  name="description"
                  required
                  rows={3}
                  defaultValue={guide.description}
                />
              </label>
              <div className="guide-inline-edit-meta">
                <label>
                  Guide date
                  <input
                    name="guideDate"
                    type="date"
                    required
                    defaultValue={guide.guideDate}
                  />
                </label>
                <label>
                  Visibility
                  <select name="visibility" defaultValue={guide.visibility}>
                    <option value="private">Private</option>
                    <option value="public">Public when published</option>
                  </select>
                </label>
              </div>
              <div className="inline-edit-actions">
                <button
                  className="button button-small"
                  disabled={guideSaveState === "saving"}
                >
                  {guideSaveState === "saving" ? "Saving…" : "Save"}
                </button>
                <button
                  className="button button-ghost button-small"
                  type="button"
                  onClick={() => setEditingGuide(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              <h1>{guide.title}</h1>
              <p className="detail-description">{guide.description}</p>
              <div className="detail-meta">
                <span className="status-badge">{guide.visibility}</span>
                <span className={`status-badge ${guide.status}`}>
                  {guide.status}
                </span>
                <span>
                  {new Date(`${guide.guideDate}T12:00:00`).toLocaleDateString()}
                </span>
                <span>Guide #{guide.code}</span>
              </div>
            </>
          )}
        </article>

        {error ? (
          <div className="alert alert-error compact-alert" role="alert">
            {error}
          </div>
        ) : null}

        {guide.canManage &&
        (guide.pdfDescriptionStatus === "pending" ||
          guide.pdfDescriptionStatus === "processing") ? (
          <div className="alert compact-alert" role="status">
            <strong>Preparing descriptions.</strong> You can continue reviewing
            positions.
          </div>
        ) : null}
        {guide.canManage && guide.pdfDescriptionStatus === "failed" ? (
          <div className="alert alert-warning compact-alert" role="alert">
            <strong>Descriptions need review.</strong> Description processing
            did not finish. Add or edit descriptions in the agenda table.
          </div>
        ) : null}

        {guide.canManage ? (
          <section
            className="publication-workflow"
            aria-labelledby="publication-workflow-title"
          >
            <div className="publication-workflow-heading">
              <div>
                <p className="kicker">Publication workflow</p>
                <h2 id="publication-workflow-title">Prepare the guide</h2>
              </div>
              <button
                className="button"
                disabled={!publicationReady || publishState === "saving"}
                onClick={() => void publish()}
              >
                {publishState === "saving"
                  ? "Publishing…"
                  : guide.version > 0
                    ? "Publish new version"
                    : "Publish positions"}
              </button>
            </div>
            <ol className="publication-steps">
              <li className="complete">
                <span className="publication-step-number">1</span>
                <span>
                  <strong>Guide details</strong>
                  <small>Ready</small>
                </span>
              </li>
              <li
                className={
                  guide.guideItemCount > 0 && !descriptionsInProgress
                    ? "complete"
                    : "current"
                }
              >
                <span className="publication-step-number">2</span>
                <span>
                  <strong>Agenda</strong>
                  <small>
                    {descriptionsInProgress
                      ? "Preparing descriptions"
                      : `${guide.guideItemCount.toLocaleString()} item${guide.guideItemCount === 1 ? "" : "s"}`}
                  </small>
                </span>
              </li>
              <li
                className={
                  publicationReady
                    ? "complete"
                    : guide.guideItemCount > 0 && !descriptionsInProgress
                      ? "current"
                      : ""
                }
              >
                <span className="publication-step-number">3</span>
                <span>
                  <strong>Positions</strong>
                  <small>
                    {adoptedPositionCount.toLocaleString()} of{" "}
                    {publicationRoundCount.toLocaleString()} set
                  </small>
                </span>
              </li>
              <li
                className={
                  guide.status === "published"
                    ? "complete"
                    : publicationReady
                      ? "current"
                      : ""
                }
              >
                <span className="publication-step-number">4</span>
                <span>
                  <strong>Publication</strong>
                  <small>
                    {guide.status === "published"
                      ? `Version ${String(guide.version)}`
                      : guide.version > 0
                        ? `Draft after version ${String(guide.version)}`
                        : publicationReady
                          ? "Ready"
                          : "Draft"}
                  </small>
                </span>
              </li>
            </ol>
          </section>
        ) : null}

        <section
          className={`guide-items-section dense-agenda-section${loadingGuide ? " is-refreshing" : ""}`}
          aria-busy={loadingGuide}
        >
          <div className="section-heading dense-section-heading">
            <div>
              <p className="kicker">Agenda</p>
              <h2>
                {guide.guideItemCount.toLocaleString()} agenda item
                {guide.guideItemCount === 1 ? "" : "s"}
              </h2>
            </div>
          </div>

          <label className="agenda-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span className="sr-only">Search agenda items</span>
            <input
              value={itemQueryDraft}
              onChange={(event) => setItemQueryDraft(event.target.value)}
              placeholder="Search item number or description"
            />
            <span>
              {loadingGuide
                ? "Updating…"
                : `${guide.items.length.toLocaleString()} of ${guide.itemTotal.toLocaleString()} shown`}
            </span>
          </label>

          <div className="stance-matrix">
            <div className="stance-matrix-header">
              <span>Agenda item</span>
              <span>Personal position</span>
              <span>Organization position</span>
              <button
                className="comparison-header-button"
                type="button"
                onClick={() => void openComparisonDialog(0)}
              >
                {comparisonColumns[0]?.guide.ownerName ||
                  "Other organization 1"}
              </button>
              <button
                className="comparison-header-button"
                type="button"
                onClick={() => void openComparisonDialog(1)}
              >
                {comparisonColumns[1]?.guide.ownerName ||
                  "Other organization 2"}
              </button>
              <span>Actions</span>
            </div>
            <div className="dense-agenda-list">
              {guide.items.map((item) => {
                const itemTarget = `/guides/${guide.accountSlug}/${guide.code}/items/${item.code}`;
                const isEditingItem = editingItemId === item.id;
                return (
                  <div className="dense-agenda-entry" key={item.id}>
                    {guide.canManage && isEditingItem ? (
                      <form
                        className="dense-agenda-edit-form"
                        onSubmit={(event) => void saveItem(event, item.id)}
                      >
                        <div className="agenda-inline-fields">
                          <label className="agenda-number-field">
                            Agenda number
                            <input
                              name="agendaNumber"
                              maxLength={80}
                              defaultValue={item.agendaNumber ?? ""}
                            />
                          </label>
                          <label className="agenda-description-field">
                            Description
                            <textarea
                              name="description"
                              rows={2}
                              defaultValue={item.description}
                            />
                          </label>
                          <label className="agenda-source-url-field">
                            Source URL
                            <input
                              name="sourceUrl"
                              inputMode="url"
                              defaultValue={item.sourceUrl ?? ""}
                            />
                          </label>
                          <label className="agenda-source-label-field">
                            Source label
                            <input
                              name="sourceLabel"
                              defaultValue={item.sourceLabel ?? ""}
                            />
                          </label>
                        </div>
                        <div className="inline-edit-actions agenda-edit-actions">
                          <button
                            className="button button-small"
                            disabled={itemSaveStates[item.id] === "saving"}
                          >
                            {itemSaveStates[item.id] === "saving"
                              ? "Saving…"
                              : "Save"}
                          </button>
                          <button
                            className="button button-ghost button-small"
                            type="button"
                            onClick={() => setEditingItemId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="dense-agenda-row">
                        <div className="agenda-row-link">
                          <div className="agenda-identifier">
                            <Link
                              className="agenda-row-number-link"
                              to={itemTarget}
                            >
                              <strong>
                                {item.agendaNumber || `#${item.code}`}
                              </strong>
                            </Link>
                            {item.sourceUrl ? (
                              <a
                                className="agenda-identifier-source"
                                href={item.sourceUrl}
                                rel="noreferrer"
                                target="_blank"
                              >
                                <FontAwesomeIcon icon={faLink} />{" "}
                                {item.sourceLabel || "Source"}
                              </a>
                            ) : null}
                          </div>
                          {item.sourcePageStart ? (
                            item.sourcePreviewReady && item.sourcePreviewUrl ? (
                              <a
                                className="agenda-source-preview"
                                href={
                                  item.sourceAttachmentUrl ||
                                  item.sourcePreviewUrl
                                }
                                target="_blank"
                                rel="noreferrer"
                                aria-label={`Open source page ${String(item.sourcePageStart)} for ${item.agendaNumber || `item ${item.code}`}`}
                              >
                                <img
                                  src={item.sourcePreviewUrl}
                                  alt={`Source page ${String(item.sourcePageStart)}`}
                                  loading="lazy"
                                />
                                <span>
                                  {(item.sourcePages?.length ?? 0) > 1
                                    ? `${String(item.sourcePages.length)} pages`
                                    : `Page ${String(item.sourcePageStart)}`}
                                </span>
                              </a>
                            ) : (
                              <div
                                className="agenda-source-preview pending"
                                aria-label={`Source page ${String(item.sourcePageStart)} preview pending`}
                              >
                                <FontAwesomeIcon icon={faFilePdf} />
                                <span>Page {item.sourcePageStart}</span>
                              </div>
                            )
                          ) : null}
                          <Link className="agenda-row-copy" to={itemTarget}>
                            <p>
                              {item.description ||
                                (guide.pdfDescriptionStatus === "pending" ||
                                guide.pdfDescriptionStatus === "processing"
                                  ? "Adding description…"
                                  : "")}
                            </p>
                            {item.descriptionAiGenerated ? (
                              <div className="agenda-row-provenance">
                                <span className="ai-marker">
                                  <FontAwesomeIcon icon={faRobot} /> AI-assisted
                                  description
                                </span>
                              </div>
                            ) : null}
                          </Link>
                        </div>
                        <div className="stance-cell personal-stance-cell">
                          {item.rounds.map((round) => {
                            return (
                              <StanceControl
                                key={round.id}
                                label={`Personal ${round.label.toLocaleLowerCase()}`}
                                currentChoice={round.myChoice}
                                canEdit={Boolean(user && guide.canContribute)}
                                isEditing={editingVoteRoundId === round.id}
                                state={voteStates[round.id]}
                                changeLabel="Change personal position"
                                onEdit={() => {
                                  setEditingVoteRoundId(round.id);
                                  setEditingRoundId(null);
                                }}
                                onSelect={(choice) =>
                                  void votePosition(
                                    round.id,
                                    choice,
                                    round.myRationale,
                                  )
                                }
                                onCancel={() => setEditingVoteRoundId(null)}
                              />
                            );
                          })}
                        </div>
                        <div
                          className="stance-cell agenda-position-summary"
                          aria-label="Organizational positions"
                        >
                          {item.rounds.map((round) => (
                            <StanceControl
                              key={round.id}
                              label={round.label}
                              currentChoice={round.adoptedChoice}
                              canEdit={guide.canManage}
                              isEditing={editingRoundId === round.id}
                              state={adoptionStates[round.id]}
                              changeLabel="Change organizational position"
                              onEdit={() => {
                                setEditingRoundId(round.id);
                                setEditingVoteRoundId(null);
                              }}
                              onSelect={(choice) =>
                                void adoptPosition(
                                  round.id,
                                  choice,
                                  round.adoptionRationale,
                                )
                              }
                              onCancel={() => setEditingRoundId(null)}
                            />
                          ))}
                        </div>
                        <div className="stance-cell comparison-stance-cell">
                          <ComparisonStances
                            comparison={comparisonColumns[0]}
                            itemCode={item.code}
                          />
                        </div>
                        <div className="stance-cell comparison-stance-cell">
                          <ComparisonStances
                            comparison={comparisonColumns[1]}
                            itemCode={item.code}
                          />
                        </div>
                        <div className="agenda-row-actions">
                          {guide.canManage ? (
                            <button
                              className="row-edit-button"
                              type="button"
                              disabled={itemSaveStates[item.id] === "saving"}
                              aria-label={`Edit ${item.agendaNumber || `item ${item.code}`}`}
                              onClick={() => {
                                setItemSaveStates((current) => {
                                  const next = { ...current };
                                  delete next[item.id];
                                  return next;
                                });
                                setEditingItemId(item.id);
                                setEditingGuide(false);
                              }}
                            >
                              Edit
                            </button>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {guide.itemTotal === 0 ? (
            <div className="empty-state">
              <h3>No agenda items found</h3>
              <p>
                {guide.guideItemCount === 0
                  ? guide.canManage
                    ? "Add changes below or import an agenda to begin."
                    : "This guide does not contain agenda items."
                  : "Clear the search to see all agenda items."}
              </p>
            </div>
          ) : null}

          {totalPages > 1 || guide.itemTotal > 50 ? (
            <nav
              className="pagination compact-pagination"
              aria-label="Agenda pages"
            >
              <button
                disabled={itemPage <= 1}
                onClick={() => setItemPage(itemPage - 1)}
              >
                Previous
              </button>
              <label className="pagination-page-picker">
                <span>Page</span>
                <select
                  aria-label="Agenda page"
                  value={itemPage}
                  onChange={(event) => setItemPage(Number(event.target.value))}
                >
                  {Array.from(
                    { length: totalPages },
                    (_, index) => index + 1,
                  ).map((page) => (
                    <option value={page} key={page}>
                      {page.toLocaleString()}
                    </option>
                  ))}
                </select>
                <span>of {totalPages.toLocaleString()}</span>
              </label>
              <label className="pagination-page-picker">
                <span>Rows</span>
                <select
                  aria-label="Agenda rows per page"
                  value={guide.itemPageSize}
                  onChange={(event) =>
                    setItemPageSize(Number(event.target.value) as 50 | 100)
                  }
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <button
                disabled={itemPage >= totalPages}
                onClick={() => setItemPage(itemPage + 1)}
              >
                Next
              </button>
            </nav>
          ) : null}

          {guide.canManage ? (
            <div className="agenda-bottom-tools">
              <button
                className="button"
                type="button"
                disabled={addItemState === "saving"}
                onClick={() => {
                  setAddItemState(undefined);
                  setAddItemOpen((current) => !current);
                  setBulkAddRows(blankBulkAddRows());
                  setBulkEditOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> Add changes
              </button>
              {guide.items.length ? (
                <button
                  className="button button-ghost"
                  type="button"
                  disabled={bulkSaveState === "saving"}
                  onClick={() => {
                    setBulkSaveState(undefined);
                    setBulkEditOpen((current) => !current);
                    setAddItemOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faPen} /> Bulk edit shown
                </button>
              ) : null}
            </div>
          ) : null}

          {guide.canManage && addItemOpen ? (
            <form
              className="agenda-bottom-editor add-change-editor"
              onSubmit={(event) => void addItems(event)}
            >
              <div className="bulk-editor-heading">
                <h3>Add changes</h3>
                <span>Paste or type</span>
              </div>
              <div className="spreadsheet-table-wrap">
                <table className="spreadsheet-table bulk-add-table">
                  <thead>
                    <tr>
                      <th scope="col">Agenda item</th>
                      <th scope="col">Description</th>
                      <th scope="col">Source URL</th>
                      <th scope="col">Source label</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkAddRows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {bulkAddColumns.map((column, columnIndex) => (
                          <td key={column}>
                            <input
                              aria-label={`${
                                column === "agendaNumber"
                                  ? "Agenda item"
                                  : column === "sourceUrl"
                                    ? "Source URL"
                                    : column === "sourceLabel"
                                      ? "Source label"
                                      : "Description"
                              } row ${String(rowIndex + 1)}`}
                              autoFocus={rowIndex === 0 && columnIndex === 0}
                              data-bulk-add-cell={`${String(rowIndex)}-${String(columnIndex)}`}
                              inputMode={
                                column === "sourceUrl" ? "url" : undefined
                              }
                              maxLength={
                                column === "agendaNumber"
                                  ? 80
                                  : column === "sourceLabel"
                                    ? 160
                                    : undefined
                              }
                              value={row[column]}
                              onChange={(event) =>
                                updateBulkAddCell(
                                  rowIndex,
                                  column,
                                  event.target.value,
                                )
                              }
                              onPaste={(event) =>
                                pasteBulkAddCells(event, rowIndex, columnIndex)
                              }
                              onKeyDown={(event) =>
                                extendBulkAddOnTab(event, rowIndex, columnIndex)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="inline-edit-actions agenda-edit-actions">
                <button
                  className="button button-ghost button-small"
                  type="button"
                  onClick={() =>
                    setBulkAddRows((current) => [
                      ...current,
                      ...blankBulkAddRows(1),
                    ])
                  }
                >
                  Add row
                </button>
                <button
                  className="button button-small"
                  disabled={addItemState === "saving"}
                >
                  {addItemState === "saving" ? "Adding…" : "Add changes"}
                </button>
                <button
                  className="button button-ghost button-small"
                  type="button"
                  onClick={() => setAddItemOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : null}

          {guide.canManage && bulkEditOpen ? (
            <form
              className="agenda-bottom-editor bulk-agenda-editor"
              onSubmit={(event) => void bulkSaveItems(event)}
            >
              <div className="bulk-editor-heading">
                <h3>Bulk edit shown items</h3>
                <span>{guide.items.length.toLocaleString()} items</span>
              </div>
              <div className="spreadsheet-table-wrap bulk-editor-list">
                <table className="spreadsheet-table bulk-edit-table">
                  <thead>
                    <tr>
                      <th scope="col">Agenda item</th>
                      <th scope="col">Description</th>
                      <th scope="col">Source URL</th>
                      <th scope="col">Source label</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guide.items.map((item) => {
                      const itemLabel =
                        item.agendaNumber || `Item ${item.code}`;
                      return (
                        <tr key={item.id}>
                          <td>
                            <input
                              aria-label={`Agenda number for ${itemLabel}`}
                              name={`agendaNumber:${item.id}`}
                              defaultValue={item.agendaNumber ?? ""}
                              maxLength={80}
                            />
                          </td>
                          <td>
                            <input
                              aria-label={`Description for ${itemLabel}`}
                              name={`description:${item.id}`}
                              defaultValue={item.description}
                            />
                          </td>
                          <td>
                            <input
                              aria-label={`Source URL for ${itemLabel}`}
                              name={`sourceUrl:${item.id}`}
                              defaultValue={item.sourceUrl ?? ""}
                              inputMode="url"
                            />
                          </td>
                          <td>
                            <input
                              aria-label={`Source label for ${itemLabel}`}
                              name={`sourceLabel:${item.id}`}
                              defaultValue={item.sourceLabel ?? ""}
                              maxLength={160}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="inline-edit-actions agenda-edit-actions">
                <button
                  className="button button-small"
                  disabled={bulkSaveState === "saving"}
                >
                  {bulkSaveState === "saving" ? "Saving…" : "Save shown items"}
                </button>
                <button
                  className="button button-ghost button-small"
                  type="button"
                  onClick={() => setBulkEditOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
        </section>

        {guide.otherPositions.length ? (
          <section className="other-positions" id="other-positions">
            <h2>Other published positions</h2>
            {guide.otherPositions.map((item) => (
              <Link
                key={`${item.accountSlug}-${item.code}`}
                to={`/guides/${item.accountSlug}/${item.code}`}
              >
                <strong>{item.ownerName}</strong>
                <span>{item.title}</span>
              </Link>
            ))}
          </section>
        ) : null}
      </div>

      {comparisonDialogSlot !== null ? (
        <div
          className="account-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setComparisonDialogSlot(null);
          }}
        >
          <section
            ref={modalDialogRef}
            className="account-dialog comparison-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="comparison-dialog-title"
            tabIndex={-1}
          >
            <p className="kicker">Compare positions</p>
            <h2 id="comparison-dialog-title">Choose an organization agenda</h2>
            <label className="comparison-search">
              Search
              <input
                autoFocus
                value={comparisonQuery}
                onChange={(event) => setComparisonQuery(event.target.value)}
                placeholder="Organization or agenda"
              />
            </label>
            {comparisonError ? (
              <div
                className="inline-operation-message alert-error"
                role="alert"
              >
                {comparisonError}
              </div>
            ) : null}
            <div className="comparison-option-list">
              {comparisonBusy ? (
                <p className="comparison-empty">Loading agendas…</p>
              ) : filteredComparisonOptions.length ? (
                filteredComparisonOptions.map((option) => (
                  <button
                    className="comparison-option"
                    key={option.id}
                    type="button"
                    onClick={() => selectComparison(option.id)}
                  >
                    <strong>{option.ownerName}</strong>
                    <span>{option.title}</span>
                    <small>
                      {option.matchCount.toLocaleString()} matching item
                      {option.matchCount === 1 ? "" : "s"} · {option.status} ·{" "}
                      {new Date(
                        `${option.guideDate}T12:00:00`,
                      ).toLocaleDateString()}
                    </small>
                  </button>
                ))
              ) : (
                <p className="comparison-empty">No matching agendas found.</p>
              )}
            </div>
            <div className="dialog-actions">
              {comparisonColumns[comparisonDialogSlot] ? (
                <button
                  className="button button-ghost"
                  type="button"
                  onClick={() => selectComparison(null)}
                >
                  Clear column
                </button>
              ) : null}
              <button
                className="button"
                type="button"
                onClick={() => setComparisonDialogSlot(null)}
              >
                Close
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {deleteDialogOpen ? (
        <div
          className="account-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !deleteBusy) {
              setDeleteError("");
              setDeleteDialogOpen(false);
            }
          }}
        >
          <section
            ref={modalDialogRef}
            className="account-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-agenda-title"
            tabIndex={-1}
          >
            <span className="dialog-danger-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2 id="delete-agenda-title">Delete this agenda?</h2>
            <div className="dialog-caution dialog-caution-danger">
              <p>
                The agenda and its published positions will no longer be
                available in StanceKit.
              </p>
            </div>
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                setDeleteError("");
                setDeleteBusy(true);
                try {
                  await api(`/api/guides/${guide.id}`, { method: "DELETE" });
                  navigate("/library?scope=mine", { replace: true });
                } catch (caught: unknown) {
                  setDeleteError(
                    caught instanceof ApiClientError
                      ? caught.message
                      : "Unable to delete this agenda.",
                  );
                  setDeleteBusy(false);
                }
              }}
            >
              {deleteError ? (
                <div
                  className="inline-operation-message alert-error"
                  role="alert"
                >
                  {deleteError}
                </div>
              ) : null}
              <div className="dialog-actions">
                <button
                  type="button"
                  className="button button-ghost"
                  disabled={deleteBusy}
                  onClick={() => {
                    setDeleteError("");
                    setDeleteDialogOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button className="button button-danger" disabled={deleteBusy}>
                  {deleteBusy ? "Deleting…" : "Delete agenda"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
